import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Reveal, MaskedLines } from "./Reveal";
import { Loader2, CheckCircle2, CalendarDays } from "lucide-react";
import { useSchedule } from "./ScheduleModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = ["Term Life", "Whole Life", "Universal Life (IUL/UL)", "Final Expense", "Mortgage Protection", "Fixed Indexed Annuity", "Retirement Solutions", "Not Sure"];
const AMOUNTS = ["$100,000", "$250,000", "$500,000", "$750,000", "$1,000,000", "$2,000,000+", "Not Sure"];
const SITUATIONS = [
  "Protect My Family",
  "Mortgage Protection",
  "Review My Current Coverage",
  "Final Expense Coverage",
  "Retirement & Wealth Transfer",
  "Child / Grandchild Coverage",
  "Debt Free 4 Life",
  "Not Sure — I'd Like Professional Guidance",
];

export const QuoteForm = () => {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", age: "", situation: "",
    coverage: "Term Life", coverage_amount: "$500,000", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { openSchedule } = useSchedule();

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("Please add your first name, last name and email.");
      return;
    }
    if (!form.situation) {
      toast.error("Please let me know what I can help you with.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, name: `${form.first_name} ${form.last_name}`.trim() };
      await axios.post(`${API}/leads`, payload);
      setDone(true);
      toast.success("Request received — we'll be in touch within 24 hours.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 lg:gap-24">
        {/* left copy */}
        <div>
          <div className="flex items-center gap-3 mb-5">
            <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Get a Quote</span>
            <span className="h-px w-16 bg-white/10" />
          </div>
          <Reveal>
            <h2 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl mb-8">
              <MaskedLines lines={[<>Let's price</>, <>your <span className="italic text-[#d4af37]">peace of mind</span>.</>]} />
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#a3a3a3] leading-relaxed tracking-wide max-w-md">
              Share a few details and a licensed advisor will compare carriers on your behalf —
              no pressure, no cost, no obligation. A real human, within one business day.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-12 space-y-4">
            {["No cost, no obligation", "Independent — dozens of carriers", "Licensed advisor, real answers"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-[#f4f0ea]/80">
                <CheckCircle2 size={16} className="text-[#d4af37]" />
                <span className="tracking-wide text-sm">{t}</span>
              </div>
            ))}
          </Reveal>
        </div>

        {/* form */}
        <Reveal delay={0.15}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-sm p-8 lg:p-12">
            {done ? (
              <motion.div
                data-testid="quote-success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-start justify-center min-h-[420px]"
              >
                <CheckCircle2 size={44} className="text-[#d4af37] mb-6" />
                <h3 className="serif text-3xl text-[#f4f0ea] mb-4">Thank you, {form.first_name}!</h3>
                <p className="text-[#a3a3a3] leading-relaxed max-w-md mb-8">
                  I've received your request and will personally review your information before reaching
                  out. If you'd rather skip the back-and-forth, you're welcome to schedule a consultation now.
                </p>
                <button
                  onClick={openSchedule}
                  data-testid="success-schedule-btn"
                  className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] rounded-full hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400"
                >
                  <CalendarDays size={15} /> Schedule My Consultation
                  <span className="transition-transform duration-400 group-hover:translate-x-1">→</span>
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="space-y-8">
                <div>
                  <label className="mono text-[10px] uppercase tracking-[0.25em] text-[#d4af37] block mb-3">What can I help you with today? *</label>
                  <select data-testid="quote-situation" className="field" value={form.situation} onChange={update("situation")} required>
                    <option value="" disabled>Select an option…</option>
                    {SITUATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <input data-testid="quote-first-name" className="field" placeholder="First name" value={form.first_name} onChange={update("first_name")} />
                  <input data-testid="quote-last-name" className="field" placeholder="Last name" value={form.last_name} onChange={update("last_name")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <input data-testid="quote-email" type="email" className="field" placeholder="Email address" value={form.email} onChange={update("email")} />
                  <input data-testid="quote-phone" className="field" placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <input data-testid="quote-age" className="field" placeholder="Age" value={form.age} onChange={update("age")} />
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.25em] text-[#737373] block mb-3">Coverage type</label>
                    <select data-testid="quote-coverage" className="field" value={form.coverage} onChange={update("coverage")}>
                      {PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="mono text-[10px] uppercase tracking-[0.25em] text-[#737373] block mb-3">Coverage amount</label>
                    <select data-testid="quote-amount" className="field" value={form.coverage_amount} onChange={update("coverage_amount")}>
                      {AMOUNTS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
                <textarea data-testid="quote-message" className="field resize-none" rows={3} placeholder="Anything we should know? (optional)" value={form.message} onChange={update("message")} />

                <button
                  type="submit"
                  data-testid="quote-submit-button"
                  disabled={loading}
                  className="group w-full inline-flex items-center justify-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-5 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37] transition-colors duration-400 disabled:opacity-60"
                >
                  {loading ? <><Loader2 className="animate-spin" size={16} /> Submitting…</> : <>Request my quote <span className="transition-transform duration-400 group-hover:translate-x-1">→</span></>}
                </button>

                <div className="pt-2 flex items-center gap-2 text-sm">
                  <span className="text-[#737373]">Prefer to choose your own time?</span>
                  <button
                    type="button"
                    onClick={openSchedule}
                    data-testid="quote-schedule-link"
                    className="mono text-[11px] uppercase tracking-[0.15em] text-[#d4af37] hover:text-[#f0d57d] link-underline inline-flex items-center gap-1"
                  >
                    Schedule a Consultation →
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
